/**
 * 인증 상태 (token + user) 전역 store.
 *
 * - access/refresh 토큰은 localStorage 에 영속화 → 새로고침해도 로그인 유지
 * - axios 인터셉터에서 동기적으로 토큰 접근 가능하도록 plain zustand 사용
 *   (`useAuthStore.getState()`)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { UserOut } from "@/lib/api/auth";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: UserOut | null;
  setTokens: (access: string, refresh: string) => void;
  setUser: (user: UserOut | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (access, refresh) => set({ accessToken: access, refreshToken: refresh }),
      setUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
    }),
    {
      name: "ttdc-auth", // localStorage key
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);

/** 현재 인증 여부 — selector 로 컴포넌트에서 사용. */
export const selectIsAuthenticated = (s: AuthState): boolean => !!s.accessToken;
